import { Link } from "react-router-dom";

import type { ApiMeta, Player } from "@/lib/api";
import { formatAge, formatInteger, formatStat } from "@/lib/format";
import { rankInk } from "@/lib/rankInk";
import { sectionById } from "@/lib/sections";
import { useReveal } from "@/lib/useReveal";
import { SectionHeader } from "./SectionHeader";

interface RosterSectionProps {
  readonly players: readonly Player[];
  readonly meta: ApiMeta | null;
  readonly loading: boolean;
  readonly error: Error | null;
}

const COLUMNS =
  "grid-cols-[2.25rem_minmax(0,1fr)_3rem_3.25rem] " +
  "sm:grid-cols-[2.75rem_minmax(0,1fr)_3.5rem_4rem_4rem_4.5rem_2.25rem] " +
  "lg:grid-cols-[3rem_minmax(0,18rem)_4rem_5rem_5rem_5rem_6rem_6.5rem]";

function Freshness({ meta }: { readonly meta: ApiMeta | null }) {
  if (!meta) return null;

  const live = meta.source === "tetrio";
  return (
    <span className="meta flex items-center gap-2 opacity-70">
      <span
        className="inline-block h-2.5 w-2.5 border-2 border-ink"
        style={{ background: live ? "var(--color-piece-s)" : "var(--color-ink-soft)" }}
      />
      {live ? `Live from TETR.IO · ${formatAge(meta.age_seconds)}` : "Last known standings"}
    </span>
  );
}

export function RosterSection({ players, meta, loading, error }: RosterSectionProps) {
  const ref = useReveal<HTMLDivElement>(0.05);
  const section = sectionById("roster")!;

  return (
    <section id="roster" className="border-b-2 border-ink">
      <div className="shell py-16 sm:py-20 md:py-24">
        <SectionHeader section={section} kicker="Sorted by peak rank, then by VS." />

        <div ref={ref} className="reveal" data-shown="false">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-[46ch] leading-relaxed">
              Everyone's current standing on TETR.IO, pulled straight from the API.
            </p>
            <Freshness meta={meta} />
          </div>

          {error ? (
            <p className="panel px-6 py-5 leading-relaxed">
              The roster is having a moment — TETR.IO could not be reached, and no stored
              results were found. The rest of the page is unaffected.
            </p>
          ) : (
            <div className="panel overflow-hidden">
              {/* Header */}
              <div
                className={`grid ${COLUMNS} items-center gap-3 border-b-2 border-ink bg-ink
                  px-4 py-3 text-field sm:px-6`}
              >
                <span className="meta text-[0.5625rem]">#</span>
                <span className="meta text-[0.5625rem]">Player</span>
                <span className="meta text-center text-[0.5625rem]">Peak</span>
                <span className="meta text-right text-[0.5625rem]">PPS</span>
                <span className="meta hidden text-right text-[0.5625rem] sm:block">APM</span>
                <span className="meta hidden text-right text-[0.5625rem] sm:block">VS</span>
                <span className="meta hidden text-right text-[0.5625rem] lg:block">TR</span>
                <span className="hidden sm:block" />
              </div>

              {loading && players.length === 0
                ? [...Array(8)].map((_, index) => (
                    <div key={index} className="h-[52px] animate-pulse border-b border-ink/10" />
                  ))
                : players.map((player) => (
                    /* The whole row is the link, rather than just the name: a
                       table row that only responds on eight characters of text
                       is a worse target than one that responds anywhere. It is
                       a single anchor, so there is one tab stop and nothing
                       interactive is nested inside anything else. */
                    <Link
                      key={player.username}
                      to={`/players/${encodeURIComponent(player.username)}`}
                      // Without this the link's accessible name is the whole
                      // row read out as one string — "3 bennxt old man X+ 2.41
                      // 78.0 213 22140 Profile". The cells are divs, so a
                      // screen reader gets no column headings to attach those
                      // numbers to. The name is spoken; the stats stay visible.
                      aria-label={`${player.display_name}, player page`}
                      className={`group grid ${COLUMNS} items-center gap-3 border-b border-ink/12
                        px-4 py-3 transition-colors duration-75 last:border-b-0
                        hover:bg-field focus-visible:bg-field sm:px-6
                        focus-visible:outline-offset-[-3px]`}
                    >
                      <span
                        className={
                          player.rank <= 3
                            ? "display text-xl leading-none tabular-nums"
                            : "font-mono text-sm tabular-nums opacity-60"
                        }
                      >
                        {player.rank}
                      </span>

                      <span className="flex min-w-0 items-baseline gap-2">
                        <span className="truncate font-semibold underline-offset-4 group-hover:underline">
                          {player.display_name}
                        </span>
                        {player.role ? (
                          <span className="meta shrink-0 text-[0.5rem] opacity-60">
                            {player.role}
                          </span>
                        ) : null}
                      </span>

                      <span className="flex justify-center">
                        <span
                          className="border-2 border-ink px-1.5 py-1 font-mono text-[0.6875rem]
                            font-medium leading-none"
                          style={{
                            background: rankInk(player.peak_rank).background,
                            color: rankInk(player.peak_rank).foreground,
                          }}
                        >
                          {player.peak_rank_label}
                        </span>
                      </span>

                      <span className="text-right font-mono text-sm tabular-nums">
                        {formatStat(player.pps)}
                      </span>
                      <span className="hidden text-right font-mono text-sm tabular-nums opacity-75 sm:block">
                        {formatStat(player.apm, 1)}
                      </span>
                      <span className="hidden text-right font-mono text-sm tabular-nums opacity-75 sm:block">
                        {formatStat(player.vs, 1)}
                      </span>
                      <span className="hidden text-right font-mono text-sm tabular-nums opacity-75 lg:block">
                        {formatInteger(player.tr)}
                      </span>

                      {/* Only for players who have actually written something —
                          a badge that opens onto "nothing here yet" is worse
                          than no badge. Rows without one are still clickable. */}
                      <span className="hidden justify-end sm:flex">
                        {player.has_profile ? (
                          <span
                            className="meta border-2 border-ink bg-paper px-2 py-1
                              text-[0.5625rem] leading-none whitespace-nowrap
                              transition-colors duration-75
                              group-hover:bg-ink group-hover:text-field
                              group-focus-visible:bg-ink group-focus-visible:text-field"
                          >
                            <span className="hidden lg:inline">Profile </span>
                            <span aria-hidden="true">→</span>
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  ))}
            </div>
          )}

          <p className="meta mt-4 opacity-60">
            PPS pieces per second · APM attack per minute · VS versus score · TR Tetra rating
          </p>
        </div>
      </div>
    </section>
  );
}
